<template>
  <v-dialog v-model="dialogVisible" max-width="95vw" >
  <div class="wage-whole-version">
    <div class="whole-version-header">
      <div class="header-title">
        <span>전체 급여 상세</span>
        <span class="employee-count">{{ groupedMemberData.length }}명</span>
      </div>
        <button class="btn-close" @click="close">×</button>
    </div>

    <div class="whole-version-body">
      <div class="table-scroll-container">
        <table class="whole-version-table">
          <thead>
            <tr>
              <th rowspan="2" class="sticky-col col-name">성명</th>
              <th rowspan="2" class="col-info">나이</th>
              <th rowspan="2" class="col-info">근속</th>
              <th rowspan="2" class="col-hours">수가시간</th>
              <th rowspan="2" class="">시급</th>
              <th rowspan="2" class="group-end">총수가</th>
              <th colspan="9" class="group-header">급여 및 수당</th>
              <th rowspan="2" class="col-total group-end">총급여</th>
              <th colspan="2" class="group-header">과세/비과세</th>
              <th rowspan="2" class="col-profit group-end">기관수익</th>
              <th colspan="10" class="group-header">근로자 공제</th>
              <th colspan="8" class="group-header">기관 공제</th>
              <th rowspan="2" class="col-net">실수령액</th>
            </tr>
            <tr>
              <!-- 급여 및 수당 -->
              <th class="sub-header">기본급</th>
              <th class="sub-header">주휴수당</th>
              <th class="sub-header">연차수당</th>
              <th class="sub-header">식대</th>
              <th class="sub-header">장기근속</th>
              <th class="sub-header">보수교육비</th>
              <th class="sub-header">중증가산</th>
              <th class="sub-header">휴일수당</th>
              <th class="sub-header group-end">연장수당</th>
              <!-- 과세/비과세 -->
              <th class="sub-header">과세</th>
              <th class="sub-header group-end">비과세</th>
              <!-- 근로자 공제 -->
              <th class="sub-header">국민연금</th>
              <th class="sub-header">건강보험</th>
              <th class="sub-header">장기요양</th>
              <th class="sub-header">고용보험</th>
              <th class="sub-header">소득세</th>
              <th class="sub-header">지방세</th>
              <th class="sub-header">본인부담금</th>
              <th class="sub-header">기타공제</th>
              <th class="sub-header">지원공제</th>
              <th class="sub-header group-end">공제합계</th>
              <!-- 기관 공제 -->
              <th class="sub-header">국민연금</th>
              <th class="sub-header">건강보험</th>
              <th class="sub-header">장기요양</th>
              <th class="sub-header">고용보험</th>
              <th class="sub-header">실업급여</th>
              <th class="sub-header">산재보험</th>
              <th class="sub-header">퇴직금</th>
              <th class="sub-header group-end">공제합계</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(group, index) in groupedMemberData" :key="index">
              <!-- 성명 -->
              <td class="sticky-col col-name">
                <div class="name-cell">
                  <span class="name">{{ group.name }}</span>
                  <span v-if="group.isBoss" class="badge">대표</span>
                  <span v-if="isAllFamilyCaregiver(group)" class="badge family">가족</span>
                </div>
              </td>
              <!-- 나이 -->
              <td class="col-info">{{ getAge(group) }}세</td>
              <!-- 근속 -->
              <td class="col-info">{{ getWorkPeriodText(group) }}</td>
              <!-- 수가시간 -->
              <td class="col-hours">{{ formatSugaHours(getTotalSugaHours(group, index)) }}</td>
              <!-- 시급 -->
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.avgTimePay) }}</td>
              <!-- 총수가 (중증수급자 가산 포함) -->
              <td class="col-money group-end">{{ formatMoney((group.totalSugaMoney || 0) + (payrollDetails[index]?.severeCareAddition || 0)) }}</td>
              
              <!-- 급여 및 수당 -->
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.basicPay) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.holidayPay) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.vacationPay) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.mealAllowance) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.longServicePay) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.bosuEduPay) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.severeCareAddition) }}</td>
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.holidayAllowance) }}</td>
              <td class="col-money group-end">{{ formatMoney(payrollDetails[index]?.overtimeAllowance) }}</td>
              
              <!-- 총급여 -->
              <td class="col-money col-total group-end">{{ formatMoney(payrollDetails[index]?.totalPay) }}</td>
              
              <!-- 과세/비과세 -->
              <td class="col-money">{{ formatMoney(payrollDetails[index]?.taxableAmount) }}</td>
              <td class="col-money group-end">{{ formatMoney(payrollDetails[index]?.nonTaxableAmount) }}</td>
              
              <!-- 기관수익 -->
              <td class="col-money col-profit group-end" :class="getProfitClass(group, index)">
                {{ formatMoney(calculateCenterProfit(group, index)) }}
              </td>
              
              <!-- 근로자 공제 -->
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.nationalPension) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.healthInsurance) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.longTermCare) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.employmentInsurance) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.incomeTax) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.employeeDeductions?.localTax) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.boninPayAmount) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.empEtcAmount) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.supportDeduction) }}</td>
              <td class="col-money col-subtotal group-end">{{ formatMoney(payrollDetails[index]?.totalEmployeeDeduction) }}</td>
              
              <!-- 기관 공제 -->
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.nationalPension) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.healthInsurance) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.longTermCare) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.employmentInsurance) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.unemployment) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.industrialAccident) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(payrollDetails[index]?.centerDeductions?.retirement) }}</td>
              <td class="col-money col-subtotal group-end">{{ formatMoney(payrollDetails[index]?.totalCenterDeduction) }}</td>
              
              <!-- 실수령액 -->
              <td class="col-money col-net">{{ formatMoney(payrollDetails[index]?.netPay) }}</td>
            </tr>
          </tbody>
          <tfoot style="border-collapse: separate;"> 
            <tr class="total-row">
              <td class="sticky-col col-name">합계</td>
              <td class="col-info">-</td>
              <td class="col-info">-</td>
              <td class="col-hours">{{ formatSugaHours(getTotalAllSugaHours()) }}</td>
              <td class="col-money">-</td>
              <td class="col-money group-end">{{ formatMoney(getTotalAllSugaMoney()) }}</td>
              
              <!-- 급여 및 수당 합계 -->
              <td class="col-money">{{ formatMoney(getSumField('basicPay')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('holidayPay')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('vacationPay')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('mealAllowance')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('longServicePay')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('bosuEduPay')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('severeCareAddition')) }}</td>
              <td class="col-money">{{ formatMoney(getSumField('holidayAllowance')) }}</td>
              <td class="col-money group-end">{{ formatMoney(getSumField('overtimeAllowance')) }}</td>
              
              <!-- 총급여 합계 -->
              <td class="col-money col-total group-end">{{ formatMoney(getSumField('totalPay')) }}</td>
              
              <!-- 과세/비과세 합계 -->
              <td class="col-money">{{ formatMoney(getSumField('taxableAmount')) }}</td>
              <td class="col-money group-end">{{ formatMoney(getSumField('nonTaxableAmount')) }}</td>
              
              <!-- 기관수익 합계 -->
              <td class="col-money col-profit group-end" :class="getTotalProfitClass()">
                {{ formatMoney(getTotalCenterProfit()) }}
              </td>
              
              <!-- 근로자 공제 합계 -->
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'nationalPension')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'healthInsurance')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'longTermCare')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'employmentInsurance')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'incomeTax')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('employeeDeductions', 'localTax')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumField('boninPayAmount')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumField('empEtcAmount')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumField('supportDeduction')) }}</td>
              <td class="col-money col-subtotal group-end">{{ formatMoney(getSumField('totalEmployeeDeduction')) }}</td>
              
              <!-- 기관 공제 합계 -->
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'nationalPension')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'healthInsurance')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'longTermCare')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'employmentInsurance')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'unemployment')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'industrialAccident')) }}</td>
              <td class="col-money col-deduction">{{ formatMoney(getSumNestedField('centerDeductions', 'retirement')) }}</td>
              <td class="col-money col-subtotal group-end">{{ formatMoney(getSumField('totalCenterDeduction')) }}</td>
              
              <!-- 실수령액 합계 -->
              <td class="col-money col-net">{{ formatMoney(getSumField('netPay')) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';

interface PatientInfo {
  patientCode: string;
  patientName: string;
  sugaCode: string;
  sugaName: string;
  sugaMinutesKey: number | null;
  timePay: string;
  serviceDays: number;
  sugaHours: number;
  totalSugaMoney: number;
  lunchTimeCount: number;
}

interface GroupedMember {
  empCode: string;
  name: string;
  birth: string;
  gender: string;
  license: string;
  workStartDate: string;
  workEndDate: string;
  patients: PatientInfo[];
  originalItems: any[];
  totalSugaMoney: number;
  totalLunchTimeCount: number;
  isBoss: boolean;
}

interface SettingsItem {
  applyNationalPension: boolean;
  yeonPay: string;
  applyBoninPay: boolean;
  boninPay: string;
  applyHealthInsurance: boolean;
  applyEmploymentInsurance: boolean;
  applyUnemployment: boolean;
  applyRetirement: boolean;
  applyHoliday: boolean;
  applyOvertime: boolean;
  applyMeal: boolean;
  applyLongServicePay: boolean;
  longServicePay: string;
  applyBosuEduPay: boolean;
  bosuEduPay: string;
  applyEmpEtc: boolean;
  empEtc: string;
  timePays: Record<string, Record<string, string>>;
  customSugaHours: Record<string, Record<string, number>>;
  sugaHoursChanged: boolean;
}

interface Props {
  modelValue: boolean;
  groupedMemberData: GroupedMember[];
  payrollDetails: Record<number, any>;
  settingsData: Record<number, SettingsItem>;
  yearMonth?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'close'): void;
}>();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const close = () => {
  dialogVisible.value = false;
  emit('close');
};

const formatMoney = (amount: number | undefined) => {
  if (!amount) return '0';
  return new Intl.NumberFormat('ko-KR').format(amount);
};

const formatSugaHours = (hours: number) => {
  if (!hours) return '0시간';
  if (hours % 1 === 0) {
    return `${hours}시간`;
  }
  return `${hours.toFixed(1)}시간`;
};

const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;
  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth, 0);
  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (
    baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const getAge = (group: GroupedMember) => {
  return calculateAge(group.birth, props.yearMonth || '');
};

const calculateWorkPeriod = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return { years: 0, months: 0 };
  const workStartDate = new Date(startDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth - 1, 1);
  let years = baseDate.getFullYear() - workStartDate.getFullYear();
  let months = baseDate.getMonth() - workStartDate.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  return { years, months };
};

const getWorkPeriodText = (group: GroupedMember) => {
  const period = calculateWorkPeriod(group.workStartDate, props.yearMonth || '');
  if (period.years > 0) {
    return `${period.years}년${period.months}월`;
  }
  return `${period.months}개월`;
};

const getSugaKey = (patient: PatientInfo): string => {
  const hoursKey = patient.sugaHours ? `_h${patient.sugaHours}` : '';
  if (patient.sugaMinutesKey != null) {
    return `${patient.sugaCode}_${patient.sugaMinutesKey}${hoursKey}`;
  }
  return `${patient.sugaCode}${hoursKey}`;
};

const is270MinutesOverSuga = (patient: { sugaName?: string; sugaMinutesKey?: number | null }) => {
  return patient.sugaMinutesKey != null || (patient.sugaName && patient.sugaName.includes('270분초과'));
};

// 8시간 초과 시에는 휴게시간 차감 없음
const getPatientSugaHours = (patient: { sugaName?: string; sugaHours?: number; sugaMinutesKey?: number | null }) => {
  const baseHours = patient.sugaHours || 0;
  if (is270MinutesOverSuga(patient)) {
    if (baseHours > 8) {
      return baseHours; // 8시간 초과 시 휴게시간 차감 없음
    }
    return Math.max(0, baseHours - 0.5);
  }
  return baseHours;
};

const getTotalSugaHours = (group: GroupedMember, groupIndex: number) => {
  let totalHours = 0;
  group.patients.forEach((patient) => {
    let sugaHours: number;
    const settings = props.settingsData[groupIndex];
    const sugaKey = getSugaKey(patient);
    if (settings?.customSugaHours?.[patient.patientCode]?.[sugaKey] !== undefined) {
      sugaHours = settings.customSugaHours[patient.patientCode][sugaKey];
    } else {
      sugaHours = getPatientSugaHours(patient);
    }
    totalHours += (patient.serviceDays || 0) * sugaHours;
  });
  return totalHours;
};

const isAllFamilyCaregiver = (group: GroupedMember): boolean => {
  if (!group || !group.patients || group.patients.length === 0) return false;
  return group.patients.every((patient) => {
    const sugaName = patient.sugaName || '';
    return sugaName.includes('가족');
  });
};

const calculateCenterProfit = (group: GroupedMember, groupIndex: number) => {
  const payroll = props.payrollDetails[groupIndex];
  if (!payroll) return 0;
  const totalSugaMoney = (group.totalSugaMoney || 0) + (payroll.severeCareAddition || 0);  // 중증수급자 가산 포함
  const monthlyPay = payroll.totalPay || 0;
  const centerDeduction = payroll.totalCenterDeduction || 0;
  return totalSugaMoney - monthlyPay - centerDeduction;
};

const getProfitClass = (group: GroupedMember, groupIndex: number) => {
  const profit = calculateCenterProfit(group, groupIndex);
  return { positive: profit > 0, negative: profit < 0 };
};

const getTotalAllSugaHours = () => {
  let total = 0;
  props.groupedMemberData.forEach((group, index) => {
    total += getTotalSugaHours(group, index);
  });
  return total;
};

const getTotalAllSugaMoney = () => {
  let total = 0;
  props.groupedMemberData.forEach((group, index) => {
    total += (group.totalSugaMoney || 0) + (props.payrollDetails[index]?.severeCareAddition || 0);
  });
  return total;
};

const getSumField = (fieldName: string) => {
  let total = 0;
  Object.values(props.payrollDetails).forEach((payroll: any) => {
    if (payroll && payroll[fieldName] !== undefined) {
      total += payroll[fieldName] || 0;
    }
  });
  return total;
};

const getSumNestedField = (parentField: string, childField: string) => {
  let total = 0;
  Object.values(props.payrollDetails).forEach((payroll: any) => {
    if (payroll && payroll[parentField] && payroll[parentField][childField] !== undefined) {
      total += payroll[parentField][childField] || 0;
    }
  });
  return total;
};

const getTotalCenterProfit = () => {
  let total = 0;
  props.groupedMemberData.forEach((group, index) => {
    total += calculateCenterProfit(group, index);
  });
  return total;
};

const getTotalProfitClass = () => {
  const profit = getTotalCenterProfit();
  return { positive: profit > 0, negative: profit < 0 };
};
</script>

<style lang="scss" scoped>
$border: #d4d4d4;
$bg: #f5f5f5;
$text: #333;
$text-sub: #666;

.wage-whole-version {
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.whole-version-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid $border;

  .header-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    color: $text;

    .employee-count {
      background: #e5e7eb;
      color: $text-sub;
      padding: 2px 10px;
      border-radius: 2px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .btn-close {
    background: transparent;
    border: 1px solid $border;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 18px;
    color: $text-sub;
    border-radius: 2px;

    &:hover {
      background: #f3f4f6;
      color: $text;
    }
  }
}

.whole-version-body {
  flex: 1;
  overflow: hidden;
  padding: 0;
}

.table-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(90vh - 60px);
}

.whole-version-table {
  width: 100%;
  border-collapse: separate;     
  border-spacing: 0; 
  font-size: 12px;
  white-space: nowrap;

  thead {
    position: sticky;
    top: 0;
    z-index: 30;

    th {
      background: #f8fafc;
      color: $text;
      font-weight: 600;
      padding: 8px 6px;
      border: 1px solid $border;
      text-align: center;
    }

    .group-header {
      background: #e5e7eb;
      color: $text;
    }

    .sub-header {
      background: #f9fafb;
      color: $text-sub;
      font-weight: 500;
      font-size: 11px;
    }
  }

  tbody {
    tr {
      background: #fff;

      &:nth-child(even) {
        background: #fafafa;
      }

      &:hover {
        background: #f0f0f0;
      }
    }

    td {
      padding: 6px 5px;
      border: 1px solid #e5e7eb;
      text-align: right;
      color: $text;
    }
    .sticky-col {
      position: sticky;
      left: 0;
      z-index: 20;
      background: #fff;
      border-right: 2px solid $border;
    }

    tr:nth-child(even) .sticky-col {
      background: #fafafa;
    }

    tr:hover .sticky-col {
      background: #f0f0f0;
    }
  }

  tfoot {
    position: sticky;
    bottom: 0;
    z-index: 25;

    .total-row {
      background: #f8fafc;
      color: $text;
      font-weight: 600;

      td {
        padding: 8px 5px;
        border: 1px solid $border;
        background: #f8fafc;
      }

      .sticky-col {
        position: sticky;
        left: 0;
        z-index: 30;
        background: #f8fafc;
        border-right: 2px solid $border;
      }

      .col-subtotal {
        background: #f3f4f6;
        color: $text;
      }

      .col-total {
        background: #f3f4f6;
        color: $text;
      }

      .col-deduction {
        color: $text-sub;
      }

      .col-profit {
        background: #f8fafc;
        &.positive { color: #059669; }
        &.negative { color: #dc2626; }
      }

      .col-net {
        background: #f8fafc;
        color: #059669;
        font-weight: 700;
      }
    }
  }

  thead .sticky-col {
    position: sticky;
    left: 0;
    z-index: 35;
    background: #f8fafc;
    border-right: 2px solid $border;
  }

  .col-name {
    min-width: 80px;
    text-align: center !important;
    padding-left: 8px !important;
  }

  .col-info {
    min-width: 45px;
    text-align: center !important;
    color: $text-sub;
  }

  .col-hours {
    min-width: 55px;
    text-align: center !important;
  }

  .col-money {
    min-width: 65px;
    text-align: right !important;
  }

  .name-cell {
    display: flex;
    align-items: center;
    gap: 4px;

    .name {
      font-weight: 500;
      color: $text;
    }

    .badge {
      background: #6b7280;
      color: #fff;
      padding: 1px 4px;
      border-radius: 2px;
      font-size: 10px;
      font-weight: 500;

      &.family {
        background: #9ca3af;
      }
    }
  }

  // tbody 전용 스타일
  tbody {
    .col-subtotal {
      background: #f9fafb;
      font-weight: 600;
    }

    .col-total {
      font-weight: 600;
      color: $text;
    }

    .col-profit {
      font-weight: 500;
      &.positive { color: #059669; }
      &.negative { color: #dc2626; }
    }

    .col-net {
      font-weight: 600;
      color: #059669;
    }

    .col-deduction {
      color: $text-sub;
    }
  }

  // 그룹 구분선
  .group-end {
    border-right: 2px solid $border !important;
  }
}
</style>
